import type { DettaglioVeicoloDTO } from 'src/api/models/DettaglioVeicoloDTO';
import type { VehicleFilterDTO } from 'src/api/models/VehicleFilterDTO';

export type MotornetVehicleKind = 'auto' | 'vcom' | 'moto';

export const MIN_MANUFACTURE_CODE_LENGTH = 3;

export interface MotornetCodeLabel {
	codice: string;
	descrizione: string;
}

export interface MotornetIdLabel {
	id: number;
	descrizione: string;
}

export interface MotornetCacheMeta {
	status: string;
	fetchedAt: string;
	softExpiresAt: string;
	hardExpiresAt: string;
	version: string;
}

export interface MotornetFormula {
	op: string | null;
	ids: number[];
	operations: MotornetFormula[];
}

export interface MotornetVincolo {
	prezzo: number;
	formula: string;
	prezzoSvalutato: number;
	formulaStrutturata: MotornetFormula | null;
	formulaNormalizzata: string | null;
}

export interface MotornetImage {
	url: string;
	risoluzione: string;
	codiceVisuale: string;
	codiceFotografia: string;
	descrizioneVisuale: string;
}

export interface MotornetPriceDetails {
	dataListino: string;
	messaSustrada: number;
	prezzoListino: number;
	prezzoListinoNetto: number;
}

export interface MotornetAccessory {
	codice: string;
	prezzo: number | null;
	idGruppo: number;
	versione: string;
	dataListino: string;
	descrizione: string;
	macrogruppo: string;
	codiceGruppo: string;
	idAccessorio: number;
	idDizionario: number;
	svalutazione: number | null;
	accessorioVin: unknown | null;
	idMacrogruppo: number;
	prezzoListino: number | null;
	prezzoListinoNetto: number;
	codiceMotornet: string | null;
	formulaVincoli: MotornetVincolo[];
	idDisponibilita: number;
	descrizioneBreve: string;
	codiceMacrogruppo: string;
	descrizioneGruppo: string;
	formulaEsclusione: string;
	formulaInclusione: string;
	idEquipaggiamento: number;
	codiceNormalizzato: string;
	idDizionarioGruppo: number;
	codiceDisponibilita: string;
	descrizioneMotornet: string | null;
	idAccessorioEsterno: number;
	descrizioneNormalizzata: string;
	descrizioneDisponibilita: string;
	formulaVincoliStrutturata: MotornetFormula | null;
	descrizioneEquipaggiamento: string;
	formulaEsclusioneStrutturata: MotornetFormula | null;
	formulaInclusioneStrutturata: MotornetFormula | null;
	formulaEsclusioneNormalizzata: string | null;
	formulaInclusioneNormalizzata: string | null;
	prevedeEsclusioneLogicaGruppo: boolean;
}

export interface MotornetModel {
	hc: string;
	hp: number;
	kw: number;
	nox: string;
	euro: string;
	foto: string | null;
	peso: number;
	pm10: number | null;
	tipo: MotornetCodeLabel;
	wltp: number | null;
	freni: MotornetCodeLabel | null;
	guado: number | null;
	marca: {
		logo: string | null;
		nome: string;
		acronimo: string;
	};
	passo: number;
	porte: number;
	posti: number;
	cambio: MotornetCodeLabel;
	codVel: string;
	coppia: string;
	traino: number;
	volumi: string;
	altezza: number;
	modello: string;
	portata: number | null;
	ridotte: boolean;
	valvole: number;
	cilindri: string;
	immagine: string;
	postiMax: number | null;
	segmento: MotornetCodeLabel;
	tipoCons: string;
	trazione: MotornetCodeLabel;
	velocita: number;
	capSerbKg: number | null;
	categoria: MotornetCodeLabel | null;
	emissUrbe: number;
	larghezza: number;
	lunghezza: number;
	paeseProd: string;
	pesoVuoto: string;
	sospPneum: number;
	tipoGuida: string;
	bagagliaio: string;
	cilindrata: number;
	garanziaKm: number | null;
	nomeCambio: string;
	nomeMotore: string;
	numeroGiri: number;
	serieGamma: MotornetCodeLabel;
	tipoMotore: string;
	dataListino: string;
	pendenzaMax: number | null;
	pesoPotenza: string;
	allestimento: string;
	architettura: MotornetCodeLabel;
	capSerbLitri: number;
	codiceMotore: string;
	consumoMedio: number | null;
	coppiaIbrido: string;
	coppiaTotale: string;
	emissioniCo2: string;
	gammaModello: MotornetCodeLabel;
	indiceCarico: string;
	massaPCarico: string;
	motoreIbrido: MotornetIdLabel | null;
	neoPatentati: boolean;
	nomeTrazione: string | null;
	prezzoMinimo: number | null;
	tipoBatteria: string;
	accelerazione: number;
	alimentazione: MotornetCodeLabel;
	altezzaMinima: number | null;
	cavalliIbrido: number | null;
	cavalliTotale: number | null;
	codiceEurotax: string;
	consumoUrbano: number | null;
	emissExtraurb: number;
	garanziaTempo: number | null;
	gruppoStorico: MotornetCodeLabel;
	potenzaIbrido: number | null;
	potenzaTotale: number | null;
	prezzoListino: number;
	tempiGiacenza: number;
	accessoriSerie: unknown[];
	autonomiaMedia: number | null;
	cavalliFiscali: number;
	codDescModello: MotornetCodeLabel;
	codiceMotornet: string;
	fineProduzione: string | null;
	ricaricaVeloce: string;
	codiceCategoria: string | null;
	equipaggiamento: string;
	motoreElettrico: string | null;
	nomeCarrozzeria: string | null;
	prezzoAccessori: number | null;
	siglaCilindrata: string;
	tipologiaMotore: string;
	autonomiaMassima: number | null;
	descrizioneBreve: string;
	descrizioneMarce: string;
	inizioProduzione: string;
	nomeAllestimento: string;
	nomeMotoreIbrido: string;
	numeroGiriIbrido: number | null;
	numeroGiriTotale: number | null;
	ricaricaStandard: string;
	allestimentoBreve: string;
	codiceCostruttore: string;
	descrizioneMotore: string;
	accessoriOpzionali: unknown[];
	consumoExtraurbano: number | null;
	cavalliElettricoMax: number | null;
	nomeMotoreElettrico: string | null;
	pneumaticiAnteriori: string;
	potenzaElettricoMax: number | null;
	descrizioneCategoria: string | null;
	pneumaticiPosteriori: string;
	capacitaNettaBatteria: number | null;
	codiceMotornetUnivoco: string;
	tipologiaMotoreIbrido: string;
	descrizioneModelloBreve: string;
	fineCommercializzazione: string | null;
	modelloBreveCarrozzeria: MotornetIdLabel;
	capacitaNominaleBatteria: number | null;
	cavalliElettricoBoostMax: number | null;
	descrizioneArricchimento: string;
	potenzaElettricoBoostMax: number | null;
	tipologiaMotoreElettrico: string | null;
	inizioCommercializzazione: string;
}

export interface MotornetVehicleDetails {
	modello: MotornetModel;
	accessiDisponibili: number;
}

export interface MotornetVehiclePayload {
	year: number;
	month: number;
	images: MotornetImage[];
	details: MotornetVehicleDetails;
	accessories: MotornetAccessories;
	eurotaxCode: string;
	vehicleKind: MotornetVehicleKind;
	priceListDates: MotornetPriceDetails[];
	manufactureCode: string;
	currentPriceDetails: MotornetPriceDetails;
}

export type MotornetVehicleResponse = MotornetVehiclePayload;

export type MotornetVehicleByManufactureCodeResponse = MotornetVehicleResponse;

export interface MotornetManufactureCodeMatch {
	codiceEurotax: string;
	codiceMotornet: string;
	da: string;
	a: string | null;
	inizioProduzione: string;
	fineProduzione: string | null;
	idModelloBreveCarrozzeria: number;
	versioneBase: MotornetIdLabel;
	descrizioneModelloBreveCarrozzeria: string;
	codiceModello: string;
	versione: string;
	tipo: string;
	tipoVeicolo: string | null;
	passo: number;
	prezzoVendita: number;
	porte: number;
	codiceCostruttore: string;
	codiciCostruttoreAlternativi: string[];
	marca: {
		acronimo: string;
		nome: string;
		logo: string | null;
	};
}

export interface MotornetManufactureCodeAmbiguousError {
	statusCode: number;
	error: 'MOTORNET_MANUFACTURE_CODE_AMBIGUOUS';
	message: string;
	manufactureCode: string;
	vehicleKind: MotornetVehicleKind;
	matches: MotornetManufactureCodeMatch[];
}

export type MotornetVehicleByManufactureCodeResult =
	| MotornetVehicleByManufactureCodeResponse
	| MotornetManufactureCodeAmbiguousError;

export function isMotornetManufactureCodeAmbiguous(
	result: MotornetVehicleByManufactureCodeResult
): result is MotornetManufactureCodeAmbiguousError {
	return (
		(result as MotornetManufactureCodeAmbiguousError).error ===
		'MOTORNET_MANUFACTURE_CODE_AMBIGUOUS'
	);
}

export interface MotornetAutosuggestMatch extends MotornetManufactureCodeMatch {
	vehicleKind: MotornetVehicleKind;
}

export interface MotornetAutosuggestResponse {
	manufactureCode: string;
	triedVehicleKinds: MotornetVehicleKind[];
	count: number;
	matches: MotornetAutosuggestMatch[];
	errors: Record<string, unknown>;
}

export interface MotornetErrorResponse {
	statusCode: number;
	error: string;
	message: string;
}

export interface MotornetVehicleOption {
	value: string;
	label: string;
	manufactureCode: string;
	vehicleKind: MotornetVehicleKind;
	match: MotornetAutosuggestMatch;
	/** ES: inizio produzione, con cui si chiede il veicolo. */
	year?: number;
	month?: number;
}

export type MotornetDettaglioVeicoloDTO = DettaglioVeicoloDTO & {
	codice_motornet?: string;
	codice_eurotax?: string;
};

export interface ColorOption {
	value: string;
	label: string;
	description: string;
	price: number | null;
	idDizionario: number | null;
	/** presente solo per i colori a catalogo (vernici/interni a pagamento) */
	idAccessorio: number | null;
	isStandard?: boolean;
}

export interface OptionalCatalogItem {
	idAccessorio: number;
	idDizionario: number;
	codice: string;
	descrizione: string;
	descrizioneEquipaggiamento: string;
	descrizioneNormalizzata: string;
	descrizioneBreve: string;
	prezzo: number | null;
	/** Voce del bucket `serie`: compresa nel prezzo di listino. */
	isStandard?: boolean;
}

export interface OptionalCatalogOptions {
	includePackages?: boolean;
	/** Aggiunge in coda l'equipaggiamento di serie, marcato `isStandard`. */
	includeStandard?: boolean;
}

export interface MotornetIdentity {
	codice_costruttore: string;
	codice_motornet: string;
	codice_eurotax: string;
	prezzo_listino: number;
	displayName: string;
}

export interface SpecItem {
	key: string;
	value: string;
}

export interface MotornetVehicleSummary {
	title: string;
	codiceCostruttore: string;
	codiceMotornet: string;
	codiceEurotax: string;
	prezzoListino: number;
	specs: SpecItem[];
}

export interface MotornetMake {
	acronimo: string;
	nome: string;
	logo: string | null;
}

export interface MotornetMakeWithKind extends MotornetMake {
	vehicleKind: MotornetVehicleKind;
}

export interface MotornetModelSummary {
	marca: string | null;
	gammaModello: MotornetCodeLabel;
	inizioProduzione: string | null;
	fineProduzione: string | null;
	gruppoStorico: MotornetCodeLabel;
	serieGamma: MotornetCodeLabel;
	codDescModello: MotornetCodeLabel;
	inizioCommercializzazione: string | null;
	fineCommercializzazione: string | null;
	modello: string;
	foto: string | null;
	prezzoMinimo: number | null;
	modelloBreveCarrozzeria: string | null;
	vehicleKind?: MotornetVehicleKind;
	[key: string]: unknown;
}

export interface MotornetVersionSummary {
	codiceEurotax: string;
	codiceMotornet: string;
	da: string | null;
	a: string | null;
	inizioProduzione: string | null;
	fineProduzione: string | null;
	nome: string;
	marca: MotornetMake;
	vehicleKind?: MotornetVehicleKind;
	versione?: string;
	codiceCostruttore?: string;
	codiciCostruttoreAlternativi?: string[];
	[key: string]: unknown;
}

export interface MotornetVersion {
	codiceEurotax?: string;
	codiceMotornet?: string;
	codiceCostruttore?: string;
	codiciCostruttoreAlternativi?: string[];
	versione?: string;
	da?: string;
	a?: string;
	[key: string]: unknown;
}

export interface MotornetVersionsListByMarca {
	acronimoMarca: string;
	numeroVersioni: number;
	versioni: MotornetVersion[];
}

export interface MotornetVersionsListPagination {
	pagina: number;
	numeroRecords: number;
}

export interface MotornetVersionsListResponsePagination {
	pagina: MotornetVersionsListPagination;
	recordsTrovati: number;
	totaleRecords: number;
}

export interface MotornetVersionsListResponse {
	pagina: MotornetVersionsListResponsePagination;
	versioniMarca: MotornetVersionsListByMarca[];
	modelloBreveCarrozzeria?: string[];
	[key: string]: unknown;
}

export interface MotornetVersionsListRequest {
	marca: string;
	modello: string;
	vehicleKind: MotornetVehicleKind;
	pagina?: {
		pagina?: number;
		numeroRecords?: number;
	};
}

export type MotornetAccessoryKind =
	| 'serie'
	| 'vernici'
	| 'optionals'
	| 'pacchetti'
	| 'accessoriVinNonCorrispondenti';

export type MotornetTipoVincolo = 'esclusione' | 'inclusione' | 'vincolo';

export type MotornetVehicleOptionValidationErrorCode =
	| 'UNKNOWN_OPTION'
	| 'SERIE_NOT_SELECTABLE'
	| 'OPTION_DISABLED_BY_MOTORNET_RULE';

export interface MotornetVehicleValidatedOption {
	idAccessorio: number;
	idDizionario: number;
	codice: string;
	descrizione: string;
	tipoAccessorio: MotornetAccessoryKind;
	tipoVincolo?: MotornetTipoVincolo;
	tipoVincoloArray?: Record<string, MotornetTipoVincolo>;
}

export interface MotornetVehicleOptionValidationError {
	code: MotornetVehicleOptionValidationErrorCode;
	message: string;
	optionId: number;
	option?: MotornetVehicleValidatedOption;
	blockedBy?: MotornetVehicleValidatedOption[];
}

export interface MotornetVehicleOptionValidationResult {
	valid: boolean;
	eurotaxCode: string;
	vehicleKind: MotornetVehicleKind;
	requestedOptionIds: number[];
	selectedOptionIds: number[];
	disabledOptionIds: number[];
	excludedOptionIds: number[];
	includedOptionIds: number[];
	constrainedOptionIds: number[];
	selectedOptions: MotornetVehicleValidatedOption[];
	disabledOptions: MotornetVehicleValidatedOption[];
	errors: MotornetVehicleOptionValidationError[];
}

export interface MotornetVehicleValidateRequest {
	vehicleKind: MotornetVehicleKind;
	eurotaxCode: string;
	selectedOptionIds?: number[];
	year?: number;
	month?: number;
	forceRefresh?: boolean;
}

export interface MotornetVehicleImportValidateRequestItem {
	rowId: string;
	manufactureCode: string;
	optionsInternalCodes?: string;
	optionsExternalCodes?: string;
	optionsCodes?: string[];
}

export type MotornetVehicleImportValidateRequest =
	MotornetVehicleImportValidateRequestItem[];

export type MotornetVehicleImportValidationWarningCode =
	| 'MANUFACTURE_CODE_NOT_FOUND'
	| 'MANUFACTURE_CODE_AMBIGUOUS'
	| 'INTERNAL_COLOR_CODE_NOT_FOUND'
	| 'EXTERNAL_COLOR_CODE_NOT_FOUND'
	| 'OPTION_CODE_NOT_FOUND';

export interface MotornetVehicleImportValidationWarning {
	code: MotornetVehicleImportValidationWarningCode;
	message: string;
	optionCode?: string;
}

export interface MotornetVehicleImportValidationMatch {
	vehicleKind: MotornetVehicleKind;
	eurotaxCode: string;
	manufactureCode?: string;
	marca?: string;
	modello?: string;
	allestimento?: string;
	warnings: MotornetVehicleImportValidationWarning[];
}

export interface MotornetVehicleImportValidationRowResult {
	rowId: string;
	manufactureCode: string;
	matches: MotornetVehicleImportValidationMatch[];
	warnings: MotornetVehicleImportValidationWarning[];
	optionsCodes: string[];
	optionsInternalCodes?: string;
	optionsExternalCodes?: string;
}

export type MotornetVehicleImportValidationResponse =
	MotornetVehicleImportValidationRowResult[];

export type StatoDispEnum = 'TARGATO' | 'TELAIATO' | 'IN_ARRIVO' | 'ORDINATO';

export type StatoTypeEnum =
	| 'DISPONIBILE'
	| 'OPZIONATO'
	| 'OSCURATO'
	| 'VENDUTO'
	| 'CANCELLATO';

export type StoricoAzioneTypeEnum =
	| 'DISPONIBILE'
	| 'OPZIONAMENTO'
	| 'OPZIONAMENTO_CANCELLATO'
	| 'OPZIONAMENTO_RINNOVATO'
	| 'OPZIONAMENTO_SCADUTO'
	| 'OSCURAMENTO'
	| 'OSCURAMENTO_RINNOVATO'
	| 'OSCURAMENTO_CANCELLATO'
	| 'VENDITA'
	| 'RESO_DISPONIBILE'
	| 'PDF_CARICATO'
	| 'AGGIUNTO_AI_PREFERITI';

export type VehicleImportWarningCode = 'OPTION_CODE_NOT_FOUND' | 'NO_IMAGES';

export interface VehicleImportWarning {
	code: VehicleImportWarningCode;
	message: string;
	optionCode?: string;
}

export interface DatiVeicoloEntity {
	vehicle_id: string;
	vehicle_kind: MotornetVehicleKind;
	accelerazione: number | null;
	alimentazione: string | null;
	powertrain_type: string | null;
	is_hybrid: boolean;
	allestimento: string | null;
	altezza: number | null;
	cambio: string | null;
	capacita_vano_carico: string | null;
	carrozzeria: string | null;
	cavalli: number | null;
	cilindrata: number | null;
	codice_motornet: string | null;
	consumo_extra_urbano: number | null;
	consumo_medio: number | null;
	consumo_urbano: number | null;
	coppia: string | null;
	emissioni_co2: number | null;
	euro: number | null;
	inizio_produzione: string | null;
	kw: number | null;
	larghezza: number | null;
	lunghezza: number | null;
	marca: string | null;
	modello: string | null;
	motore: string | null;
	passo: number | null;
	porte: number | null;
	posti: number | null;
	prezzo_listino: number | null;
	segmento: string | null;
	trazione: string | null;
	velocita: number | null;
	extra_sconto_perc: number | null;
}

export interface ListaNoleggiatoriQuotatoreEntity {
	noleggiatore: string;
	is_checked: boolean;
	extra_sconto_perc?: number | null;
}

export interface DocumentEntity {
	name: string;
	provvigioni?: string | null;
	is_active?: boolean | null;
}

export interface DettaglioVeicoloEntity {
	id_bo: string | null;
	city: string | null;
	colore_esterno: string | null;
	codice_colore_esterno: string | null;
	colore_interno: string | null;
	codice_colore_interno: string | null;
	codice_costruttore: string | null;
	data_disp: string | null;
	data_immatricolazione: string | null;
	data_telaio: string | null;
	data_inserimento: string;
	dealer: string | null;
	giorno_disp: number | null;
	note: string | null;
	nr_ordine: string | null;
	promo: boolean | null;
	promo_note: string | null;
	settimana_prod: string | null;
	stato_disp: StatoDispEnum;
	targa: string | null;
	telaio: string | null;
	in_evidenza: boolean | null;
	lista_noleggiatori_quotatore: ListaNoleggiatoriQuotatoreEntity[];
	document: DocumentEntity[];
	visibility_tag: string | null;
	limited_visibility: string[] | null;
}

export interface OptionalEntity {
	codice_costruttore: string;
	descrizione: string | null;
	valore: number | null;
}

export interface StatoEntity {
	type: StatoTypeEnum;
	cliente: string | null;
	noleggiatore: string | null;
	data_delete: string | null;
	data_inizio: string | null;
	data_scadenza: string | null;
	n_estensione: number | null;
	pratica_credito: string | null;
	ragione_sociale: string | null;
	utente: string | null;
	dealer_utente: string | null;
	renewalsCounter: number | null;
	autoReleaseInDays: number | null;
	document: DocumentEntity[];
	opportunityId: string | null;
	visibility_tag: string | null;
	limited_visibility: boolean | null;
	drive_it_easy_status: string | null;
	dataFineContrattoDriveItEasy: string | null;
}

export interface StoricoAzioneEntity {
	cliente: string | null;
	timestamp: string;
	type: StoricoAzioneTypeEnum;
	utente: string | null;
	opportunityId: string | null;
}

export type OffertaEntity = Record<string, unknown>;

export interface ImageEntity {
	url: string;
}

export type WishlistEntity = Record<string, unknown>;

export type RichiestaPreventivoEntity = Record<string, unknown>;

export interface VehicleEntity {
	dati_veicolo: DatiVeicoloEntity;
	dettagli_veicolo: DettaglioVeicoloEntity;
	optionals: OptionalEntity[];
	stato_corrente: StatoEntity;
	storico_azioni: StoricoAzioneEntity[];
	lista_offerte: OffertaEntity[];
	image_list: ImageEntity[];
	preferiti: WishlistEntity[];
	richieste_preventivi: RichiestaPreventivoEntity[];
	lista_noleggiatori_quotatore: ListaNoleggiatoriQuotatoreEntity[];
	is_codice_costruttore_jato_uid: boolean;
	visibilita_segnalatore: boolean;
}

export interface PlatformVehicleImportResponse {
	success: boolean;
	vehicles: VehicleEntity[];
	insertedIds?: string[];
	warnings: VehicleImportWarning[];
	error?: string;
}

export interface PlatformVehicleImportDetail {
	codice_eurotax: string;
	codice_costruttore: string;
	dealer: string;
	codice_colore_esterno: string;
	codice_colore_interno?: string | null;
	id_bo?: string | null;
	city?: string | null;
	colore_esterno?: string | null;
	colore_interno?: string | null;
	data_disp?: string | null;
	data_immatricolazione?: string | null;
	data_telaio?: string | null;
	giorno_disp?: number | null;
	note?: string | null;
	promo?: boolean | null;
	promo_note?: string | null;
	settimana_prod?: string | null;
	targa?: string | null;
	telaio?: string | null;
	in_evidenza?: boolean | null;
	limited_visibility?: string[] | null;
	lista_noleggiatori_quotatore?: ListaNoleggiatoriQuotatoreEntity[];
	document?: DocumentEntity[];
	optionals?: OptionalEntity[];
	extra_sconto_perc?: number | null;
}

export interface PlatformVehicleImportRequest {
	dettaglio_veicolo: PlatformVehicleImportDetail;
	nr_ordine_list: string[];
	is_checked?: boolean;
	visibility_tag?: string | null;
}

export interface PlatformVehicleImportExcelRequest {
	codice_eurotax: string;
	dealer: string;
	codice_costruttore: string;
	nr_ordine: string;
	codice_colore_esterno: string;
	codice_colore_interno?: string | null;
	optional_list?: string[];
	settimana_prod?: string | null;
	data_disp?: string | null;
	city?: string | null;
	data_telaio?: string | null;
	telaio?: string | null;
	data_immatricolazione?: string | null;
	targa?: string | null;
	id_bo?: string | null;
	promo?: boolean | null;
	giorno_disp?: string | null;
	promo_note?: string | null;
	note?: string | null;
	visibility_tag?: string | null;
}

export interface PlatformQuotableResult {
	platform_uuid: string;
	jato_id: number | null;
	marca: string | null;
	vehicle_code: string | null;
	quotation_by_vehicle_codes: string[];
	matched_noleggiatori: string[];
	effective_noleggiatori: string[];
	n_noleggiatori: number;
	quotable_min: number;
	quotable: boolean;
}

export interface PlatformQuotableResponse {
	total: number;
	quotableCount: number;
	notQuotableCount: number;
	quotableIds: string[];
	notQuotableIds: string[];
	results: PlatformQuotableResult[];
}

export interface PlatformQuotationsParameters {
	vehicle_id: number | null;
	modello: string | null;
	versione_locale: string | null;
	marca: string | null;
	durata: number[];
	km_annui: number[];
	noleggiatore: string[];
	created_at: string | null;
	is_custom: boolean;
}

export interface PlatformQuotationOffer {
	noleggiatore: string;
	canone: number;
	updated_canone: number;
	percentage: number;
	commissioni: number;
	[key: string]: unknown;
}

export interface PlatformQuotationResponse {
	best_offers: PlatformQuotationOffer[];
	similar_offers: PlatformQuotationOffer[];
	ai_offers: PlatformQuotationOffer[];
}

export type EurotaxBackfillFilter =
	| 'resolved'
	| 'ambiguous'
	| 'notFound'
	| 'priceMismatch'
	| 'skipped'
	| 'manual'
	| 'neverProcessed';

export interface EurotaxBackfillPriceDetails {
	date: string;
	list_price: number;
	delivery_fee: number;
	net_list_price: number;
}

export interface EurotaxBackfillVehicle {
	platform_uuid: string;
	codice_costruttore: string | null;
	marca: string | null;
	modello: string | null;
	allestimento: string | null;
	vehicle_code: string | null;
	vehicle_kind: string | null;
	manufacture_code: string | null;
	jato_id: number | null;
	price_manual_override: boolean | null;
	current_price_details: EurotaxBackfillPriceDetails | null;
	ambiguous: boolean;
	not_found: boolean;
	price_mismatch: boolean;
	migration_debug: Record<string, unknown> | null;
}

export interface EurotaxBackfillVehiclesResponse {
	total: number;
	page: number;
	pageSize: number;
	vehicles: EurotaxBackfillVehicle[];
}

export interface EurotaxBackfillVehiclesRequest {
	filter?: EurotaxBackfillFilter;
	manufactureCode?: string;
	page?: number;
	pageSize?: number;
}

export interface EurotaxBackfillResolveRequest {
	mongoIds: string[];
	vehicle_code: string;
	vehicle_kind: MotornetVehicleKind;
	manufacture_code: string;
	make_code: string;
	model_code: string;
}

export interface EurotaxBackfillResolveResponse {
	updated: string[];
	notFound: string[];
	vehicle_code: string;
	vehicle_kind: string;
	manufacture_code: string;
	make_code: string;
	model_code: string;
}

export type EurotaxBackfillRetryOutcome =
	| 'resolved'
	| 'ambiguous'
	| 'notFound'
	| 'priceMismatch'
	| 'skipped';

export interface EurotaxBackfillRetryResponse {
	mongoId: string;
	outcome: EurotaxBackfillRetryOutcome;
}

export interface EurotaxBackfillPriceOverrideRequest {
	priceManualOverride: boolean;
}

export interface EurotaxBackfillPriceOverrideResponse {
	mongoId: string;
	priceManualOverride: boolean;
}

export interface PlatformConfigurableMake {
	acronimo: string;
	nome: string;
	logo: string | null;
}

export interface PlatformConfigurableModel {
	make: { code: string; name: string } | null;
	model: { code: string; description: string };
	image: string | null;
	fuels: string[];
	minPrice: number | null;
	bodyType: string | null;
}

export interface PlatformConfigurableListItem {
	id: string;
	vehicleCode: string | null;
	manufactureCode: string | null;
	image: string | null;
	excluded: boolean;
	fuel: string | null;
	trim: string | null;
	bodyType: string | null;
	make: string | null;
	model: string | null;
	listPrice: number | null;
	segment: string | null;
}

export interface PlatformConfigurableListResponse {
	items: PlatformConfigurableListItem[];
	total: number;
	page: number;
	pageSize: number;
}

export interface PlatformConfigurableListRequest {
	make?: string;
	model?: string;
	excluded?: boolean;
	page?: number;
	pageSize?: number;
}

export interface PlatformConfigurableExcludeResponse {
	vehicle_code: string;
	excluded: boolean;
}

export type MotornetAccessoriesResponse = {
	serie?: MotornetAccessory[];
	vernici?: MotornetAccessory[];
	optionals?: MotornetAccessory[];
	pacchetti?: MotornetAccessory[];
	accessoriVinNonCorrispondenti?: MotornetAccessory[];
};

export type MotornetAccessories = MotornetAccessoriesResponse;

export interface StockVehicle {
	id: string;
	vehicleCode: string;
	jatoId: number | null;
	vehicleKind: string;
	make: string;
	model: string;
	trim: string;
	modelYear: number | null;
	fuelCode: string | null;
	price: StockVehiclePrice;
	optionals: StockVehicleOptional[];
	colors: StockVehicleColors;
	specs: StockVehicleSpecs;
	rentalCompanies: StockVehicleRentalCompany[];
	photos: string[];
}

export interface StockVehiclePrice {
	currency: string;
	listPriceNoVat: number;
	optionalsNoVat: number;
	totalNoVat: number;
	extraScontoPerc: number;
}

export interface StockVehicleOptional {
	code: string;
	description: string;
	priceVat: number;
	priceNoVat: number;
}

export interface StockVehicleColors {
	exteriorCode: string | null;
	exteriorLabel: string | null;
	interiorCode: string | null;
	interiorLabel: string | null;
}

export interface StockVehicleSpecs {
	alimentazione: string | null;
	carrozzeria: string | null;
	cambio: string | null;
	porte: number | null;
	posti: number | null;
	cavalli: number | null;
	motore: string | null;
	kw: number | null;
	cilindrata: number | null;
	coppia: string | null;
	trazione: string | null;
	velocita: number | null;
	accelerazione: number | null;
	lunghezza: number | null;
	larghezza: number | null;
	altezza: number | null;
	passo: number | null;
	capacitaVanoCarico: string | null;
	consumoMedio: number | null;
	consumoUrbano: number | null;
	consumoExtraUrbano: number | null;
	emissioniCo2: number | null;
	euro: number | null;
}

export interface StockVehicleRentalCompany {
	code: string;
	enabled: boolean;
	extraScontoPerc: number | null;
}

export interface ConfigurableStockRequest {
	makeCode: string;
	modelCode: string;
}

export interface ConfigurableStockVehicle {
	id: string;
	dati_veicolo: ConfigurableStockDatiVeicolo;
	dettagli_veicolo: ConfigurableStockDettagliVeicolo;
	optionals: ConfigurableStockOptional[];
	image_list: ConfigurableStockImage[];
	lista_noleggiatori_quotatore: ConfigurableStockNoleggiatore[];
	currency: string;
	prezzoListinoNetto: number;
	prezzoOptionalsNetto: number;
	extraScontoPerc: number;
}

export interface ConfigurableStockDatiVeicolo {
	marca: string;
	modello: string;
	allestimento: string;
	vehicle_code: string;
	jato_id: number | null;
	vehicle_kind: string;
	model_year: number | null;
	alimentazione: string | null;
	powertrain_type: string | null;
	carrozzeria: string | null;
	cambio: string | null;
	porte: number | null;
	posti: number | null;
	cavalli: number | null;
	motore: string | null;
	kw: number | null;
	cilindrata: number | null;
	coppia: string | null;
	trazione: string | null;
	velocita: number | null;
	accelerazione: number | null;
	lunghezza: number | null;
	larghezza: number | null;
	altezza: number | null;
	passo: number | null;
	capacita_vano_carico: number | null;
	consumo_medio: number | null;
	consumo_urbano: number | null;
	consumo_extra_urbano: number | null;
	emissioni_co2: number | null;
	euro: number | null;
	prezzo_listino: number;
	extra_sconto_perc: number | null;
}

export interface ConfigurableStockDettagliVeicolo {
	colore_esterno: string | null;
	codice_colore_esterno: string | null;
	colore_interno: string | null;
	codice_colore_interno: string | null;
}

export interface ConfigurableStockOptional {
	codice_costruttore: string;
	descrizione: string;
	/** **IVA inclusa**, come salvato in HMP. */
	valore: number;
	/** Netto calcolato dal backend: la FE non ri-scorpora l'IVA. */
	valoreNetto: number;
}

export interface ConfigurableStockImage {
	url: string;
}

export interface ConfigurableStockNoleggiatore {
	noleggiatore: string;
	is_checked: boolean;
	extra_sconto_perc: number | null;
}

export interface ConfigurableStockResponse {
	total: number;
	/** Sempre 1: l'endpoint non pagina. */
	page: number;
	pageSize: number;
	items: ConfigurableStockVehicle[];
}

export interface PlatformConfigurationMetadata {
	makeCode: string;
	makeLabel: string;
	modelCode: string;
	modelLabel: string;
	trimLabel?: string | null;
	vehicleCode?: string | null;
	vehicleKind?: string | null;
	year?: number | null;
	month?: number | null;
	stockVehicleId?: string | null;
	listPrice?: number | null;
	optionalsTotal?: number | null;
	totalPrice?: number | null;
	imageUrl?: string | null;
	/** Alfanumerico: il BE valida `^[A-Za-z0-9]+$`. */
	code: string;
}

export type PlatformConfigurationUnavailableReason =
	| 'excluded'
	| 'not-quotable';

export interface PlatformConfigurationListItem {
	id: string;
	version: number;
	metadata: PlatformConfigurationMetadata;
	createdAt: string;
	updatedAt: string;
	available: boolean;
	unavailableReason: PlatformConfigurationUnavailableReason | null;
}

/** La lista non restituisce `payload`: solo il dettaglio e il salvataggio. */
export interface PlatformConfiguration extends PlatformConfigurationListItem {
	payload: Record<string, unknown>;
}

export interface PlatformConfigurationListRequest {
	q?: string;
	make?: string;
	model?: string;
	page?: number;
	pageSize?: number;
	sortBy?: 'updatedAt' | 'createdAt';
	sortDirection?: 'asc' | 'desc';
}

export interface PlatformConfigurationListResponse {
	items: PlatformConfigurationListItem[];
	total: number;
	page: number;
	pageSize: number;
}

export interface SaveConfigurationBody {
	version: number;
	metadata: PlatformConfigurationMetadata;
	payload: Record<string, unknown>;
}

export interface DeleteConfigurationResponse {
	id: string;
	deleted: boolean;
}

export type CatalogProvider = 'arval' | 'ayvens' | 'leasys' | 'alphabet';

export type CatalogVehicleKind = 'auto' | 'vcom';
export type CatalogState = 'unavailable' | 'configurable' | 'quickable';

export interface CatalogRowProvider {
	provider: CatalogProvider;
	ratebookId: string;
	make: string | null;
	model: string | null;
	version: string | null;
	modelYear: number | null;
	manufacturerCode: string | null;
	jato: string | null;
	listPrice: number | null;
	listPriceInclVAT: number | null;
	fuel: string | null;
	transmission: string | null;
	powerKw: number | null;
	bodyType: string | null;
	link: 'automatic' | 'manual';
	unavailable?: false;
}

export interface CatalogUnavailableProvider {
	provider: CatalogProvider;
	unavailable: true;
	markedBy: string;
	markedAt: string;
	reason: string | null;
}

export interface CatalogManualLink {
	provider: CatalogProvider;
	action: 'link' | 'exclude' | 'unavailable';
	active: boolean;
	ratebookId: string | null;
	label: string | null;
	reason: string | null;
	updatedAt: string;
	updatedBy: string;
}

export interface CatalogRowMotornet {
	vehicleCode: string;
	mode: 'automatic' | 'manual';
	availableUnits: number;
	quotableUnits: number;
}

export interface CatalogMatrixAutoRenew {
	enabled: boolean;
	by: string;
	at: string;
}

export interface CatalogRow {
	versionKey: string;
	snapshot: string;
	make: string | null;
	model: string | null;
	version: string | null;
	modelYear: number | null;
	vehicleKind: string | null;
	fuel: string | null;
	transmission: string | null;
	powerKw: number | null;
	listPrice: number | null;
	matrixAutoRenew: CatalogMatrixAutoRenew | null;
	providers: (CatalogRowProvider | CatalogUnavailableProvider)[];
	manualLinks: CatalogManualLink[];
	motornet: CatalogRowMotornet | null;
	manufacturerCodes?: string[] | null;
}

export interface CatalogRowFilters {
	vehicleKind?: CatalogVehicleKind;
	providers?: CatalogProvider[];
	minMatches?: number;
	state?: CatalogState;
	hasStock?: boolean;
	matrixAutoRenew?: boolean;
	search?: string;
}

export interface CatalogRowsRequest extends CatalogRowFilters {
	make?: string;
	model?: string;
	page?: number;
	pageSize?: number;
}

export interface CatalogRowsResponse {
	items: CatalogRow[];
	total: number;
	page: number;
	pageSize: number;
}

export const PLATFORM_VEHICLES_SORT_FIELDS = [
	'disponibili',
	'marca',
	'modello',
	'allestimento',
	'prezzo_listino',
	'model_year',
	'data_disp',
	'data_immatricolazione',
	'data_inserimento',
	'city',
	'dealer',
	'nr_ordine',
	'codice_costruttore',
	'colore_esterno',
	'colore_interno',
	'stato_disp',
	'targa',
	'telaio',
	'alimentazione',
	'cambio',
	'carrozzeria',
	'trazione',
	'promo',
	'in_evidenza',
	'utente',
	'stato_corrente.type',
	'offerta_minima.valore',
	'offerta_minima.noleggiatore',
] as const;

export type PlatformVehiclesSortBy =
	(typeof PLATFORM_VEHICLES_SORT_FIELDS)[number];

export interface PlatformVehiclesRequest {
	page?: number;
	pageSize?: number;
	sortBy?: PlatformVehiclesSortBy;
	sortDirection?: 'asc' | 'desc';
	quotable?: boolean;
	stellantisOnly?: boolean;
	scope?: 'my-stock';
	normalize?: boolean;
	extractModel?: boolean;
	stato_corrente?: string[];
	stato_disp?: string[];
	marca?: string;
	modello?: string;
	allestimento?: string;
	dealer?: string;
	colore_esterno?: string;
	colore_interno?: string;
	trazione?: string;
	carrozzeria?: string;
	city?: string;
	search?: string;
	telaio?: string;
	targa?: string;
	nr_ordine?: string;
	codice_costruttore?: string;
	promo?: boolean;
	in_evidenza?: boolean;
	alimentazione?: string;
	cambio?: string;
	utente?: string;
	data_disp_from?: string;
	data_disp_to?: string;
	data_immatricolazione_from?: string;
	data_immatricolazione_to?: string;
	offerta_minima_from?: string;
	offerta_minima_to?: string;
}

// `modello_not_normalized` arriva solo con normalize=true.
export type PlatformVehicleListItem = VehicleFilterDTO & {
	visibilita_segnalatore?: boolean | null;
	modello_not_normalized?: string | null;
};

export interface PlatformVehiclesResponse {
	items: PlatformVehicleListItem[];
	total: number;
	page: number;
	pageSize: number;
}

export interface CatalogFacetOption {
	value: string;
	label: string;
	count: number;
}

export interface CatalogFacetResponse {
	items: CatalogFacetOption[];
}

export interface CatalogCandidate {
	provider: CatalogProvider;
	ratebookId: string;
	make: string | null;
	model: string | null;
	version: string | null;
	manufacturerCode: string | null;
	jato: string | null;
	listPrice: number | null;
	fuel: string | null;
	transmission: string | null;
	powerKw: number | null;
	currentRow: {
		versionKey: string;
		make: string | null;
		model: string | null;
		version: string | null;
		providers: CatalogProvider[];
		motornetVehicleCode: string | null;
	} | null;
}

export interface CatalogCandidatesRequest {
	provider: CatalogProvider;
	make?: string;
	model?: string;
	version?: string;
	/** Ricerca libera: codice costruttore, JATO, ratebookId, prezzo... */
	q?: string;
	page?: number;
}

export interface CatalogCandidatesResponse {
	items: CatalogCandidate[];
	facets: { makes: string[]; models: string[]; versions: string[] };
	total: number;
	page: number;
	pageSize: number;
}

export interface CatalogLinkBody {
	versionKey: string;
	provider: CatalogProvider;
	ratebookId: string;
}

export interface CatalogRemoveBody {
	versionKey: string;
	provider: CatalogProvider;
}

export interface CatalogUnavailableBody extends CatalogRemoveBody {
	reason?: string;
}

export interface CatalogProviderChangeResponse {
	version: CatalogRow | null;
	movedFrom: { versionKey: string; label: string }[];
	vehiclesPending: boolean;
}

export interface CatalogMotornetBody {
	versionKey: string;
	snapshot: string;
	vehicleCode: string;
}

export interface CatalogMatrixAutoRenewBody {
	versionKey: string;
	snapshot: string;
	enabled: boolean;
}

export interface CatalogMotornetAutoBody {
	versionKey: string;
	snapshot: string;
}

export type CatalogMotornetAutoOutcome =
	| 'associated'
	| 'already_associated'
	| 'ambiguous'
	| 'not_found'
	| 'lookup_failed'
	| 'conflict';

export interface CatalogMotornetAutoResponse {
	outcome: CatalogMotornetAutoOutcome;
	version: CatalogRow;
}
